import { CIInfoIcon, Modal, Textarea, useToastMessage } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLoader from '../../../common/hooks/Loader';
import { changeJourneyStatus } from '../../store/_api';
import { JourneyComments } from '../../store/_api/TypeDefs';
import JourneyStepModalBody from './JourneyStepModalBody';

import { NotifyDialogStatus } from '../../modules/journey-canvas/utility/TypeDefs';
import classes from './JourneyCanvasModals.module.scss';

interface JourneyStepDetailsProps {
  showJourneyStepModal: boolean;
  values: {
    journeyStepName: string;
    maxExecutionTimeInput: {
      hours: string;
      minutes: string;
      seconds: string;
    };
    uniqueIdentifiers: string[] | undefined;
    journeyStepDescription: string | undefined;
    endpointNodeId: string | undefined;
  },
  saveStepModalHandler: (
    journeyStepName: string,
    maxExecutionTimeInput: {
      hours: string;
      minutes: string;
      seconds: string;
    },
    uniqueIdentifiers: string[],
    journeyStepDescription: string | undefined,
    endpointNodeId: string | undefined,
    isEdit: boolean,
  ) => void
  closeStepModalHandler: () => void;
  dropdownEndpointValues: {
    value: string;
    name: string;
  }[];
  readonly: boolean;
  cloning?: boolean;
}

interface ConfirmErrorProps {
  notifyApiError: boolean;
  confirmErrorHandler: () => void
}

interface ActiveStatusEditWarningProps {
  showActiveStatusModal: boolean;
  proceedActiveStatus: () => void;
  cancelActiveStatus: () => void;
}

interface CemCancelWarningProps {
  showCancelWarningModal: boolean;
  leaveCancelWarningModal: () => void;
  setShowCancelWarningModal: React.Dispatch<React.SetStateAction<boolean>>
}

interface ApproveWarningProps {
  showApproveWarningModal: boolean;
  journeyVersion: number;
  setShowApproveWarningModal: React.Dispatch<React.SetStateAction<boolean>>;
  journeyName: string;
  goToPrevPage: (isUpdated?: boolean) => void;
  journeyComments?: JourneyComments[];
}

interface ReturnJourneyProps {
  showReturnModal: boolean;
  setShowReturnModal: React.Dispatch<React.SetStateAction<boolean>>;
  journeyName: string;
  journeyVersion: number;
  goToPrevPage: (isUpdated?: boolean) => void
}

interface SendForApproveProps {
  showSendForApprovalModal: boolean;
  journeyName: string;
  journeyVersion: number;
  setShowSendForApprovalWarningModal: React.Dispatch<React.SetStateAction<boolean>>;
  goToPrevPage: (isUpdated?: boolean) => void
}

interface MoveToDraftProps {
  showMoveToDraftModal: boolean;
  journeyName: string;
  journeyVersion: number;
  setShowMoveToDraftWarningModal: React.Dispatch<React.SetStateAction<boolean>>;
  goToPrevPage: (isUpdated?: boolean) => void
}

interface SaveJourneyToDesignCemProps {
  showSaveJourneyToDesignCemModal: boolean;
  closeSaveJourneyModal: () => void;
}

interface ConfirmDeleteProps {
  notifyDialogStatus: NotifyDialogStatus | undefined
  designingCEM: boolean,
  showNotifyDialog: boolean,
  confirmDelete: () => void,
  hideDeleteNotifyDialogHandler: () => void
}

interface ExternalOnSubsequentStepModalProps {
  show: boolean;
  onDismiss: () => void;
}

export enum JourneyStepNameValidation {
  VALID = 'VALID',
  INVALID = 'INVALID',
  LENGTH_EXCEEDED = 'LENGTH_EXCEEDED',
  EMPTY = 'EMPTY'
}

export const JourneyStepDetailsModal: React.FC<JourneyStepDetailsProps> = ({ showJourneyStepModal, values, saveStepModalHandler, closeStepModalHandler, dropdownEndpointValues, readonly, cloning = false }: JourneyStepDetailsProps) => {
  const { t: translate } = useTranslation();

  const [journeyStepName, setJourneyStepName] = useState<string>('');
  const [invalidJourneyStepName, setInvalidJourneyStepName] = useState<string>(JourneyStepNameValidation.VALID);
  const [maxExecutionTimeInput, setMaxExecutionTimeInput] = useState<{ hours: string, minutes: string, seconds: string }>({ hours: '0', minutes: '0', seconds: '0' });
  const [uniqueIdentifiers, setUniqueIdentifiers] = useState<string[]>([]);
  const [journeyStepDescription, setJourneyStepDescription] = useState<string>();
  const [endpointNodeId, setEndpointNodeId] = useState<string>();

  const isEdit = !!values.journeyStepName;

  const saveStepDetails = () => {
    saveStepModalHandler(journeyStepName, maxExecutionTimeInput, uniqueIdentifiers, journeyStepDescription, endpointNodeId, isEdit);
  };

  useEffect(() => {
    if (showJourneyStepModal) {
      setJourneyStepName(values.journeyStepName);
      setMaxExecutionTimeInput(values.maxExecutionTimeInput);
      setUniqueIdentifiers(values.uniqueIdentifiers ?? []);
      setJourneyStepDescription(values.journeyStepDescription);
      setEndpointNodeId(values.endpointNodeId);
    }
  }, [showJourneyStepModal, values]);

  return (
    <Modal
      modalTitle={translate('journey_builder_component.lbl_step_details')}
      modalBody={
        <JourneyStepModalBody
          journeyStepName={journeyStepName}
          setJourneyStepName={setJourneyStepName}
          invalidJourneyStepName={invalidJourneyStepName}
          maxExecutionTimeInput={maxExecutionTimeInput}
          setMaxExecutionTimeInput={setMaxExecutionTimeInput}
          uniqueIdentifiers={uniqueIdentifiers}
          setUniqueIdentifiers={setUniqueIdentifiers}
          journeyStepDescription={journeyStepDescription}
          setJourneyStepDescription={setJourneyStepDescription}
          setInvalidJourneyStepName={setInvalidJourneyStepName}
          dropdownEndpointValues={dropdownEndpointValues}
          endpointNodeId={endpointNodeId}
          setEndpointNodeId={setEndpointNodeId}
          isEdit={isEdit}
          readonly={readonly}
          cloning={cloning}
        />
      }
      modalShow={showJourneyStepModal}
      btnSaveText={String(translate(isEdit ? 'journey_builder_component.btn_save' : 'journey_builder_component.lbl_save_step_details'))}
      onSaveModal={saveStepDetails}
      onCloseModal={closeStepModalHandler}
      alignToCentre
      data-testid="saveJourneyOnClick"
      disableSaveBtn={readonly || !journeyStepName || invalidJourneyStepName !== JourneyStepNameValidation.VALID || (dropdownEndpointValues.length > 0 && !endpointNodeId)}
    />
  );
};

export const ConfirmErrorModal: React.FC<ConfirmErrorProps> = ({ notifyApiError, confirmErrorHandler }: ConfirmErrorProps) => {
  const { t: translate } = useTranslation();

  return (
    <Modal
      modalTitle={translate('journey_builder_component.lbl_confirm_error_title')}
      modalBody={
        <div className={classes.modal} data-testid="apiErrorModal">
          <div className={classes['modal-journey-name']}>
            {translate('journey_builder_component.msg_api_error')}
          </div>
        </div>
      }
      modalShow={notifyApiError}
      btnSaveText={String(translate('journey_builder_component.lbl_confirm_error_onSave'))} // confirmation needed
      onSaveModal={confirmErrorHandler}
      onCloseModal={confirmErrorHandler}
      alignToCentre
      data-testid="apiErrorOnClick"
      disableSaveBtn={false}
      type="warning"
    />
  );
};

export const ActiveStatusEditWarningModal: React.FC<ActiveStatusEditWarningProps> = ({ showActiveStatusModal, proceedActiveStatus, cancelActiveStatus }: ActiveStatusEditWarningProps) => {
  const { t: translate } = useTranslation();

  return (
    <Modal
      modalTitle={translate('journey_builder_component.lbl_new_version')}
      modalBody={translate('journey_builder_component.msg_update_Active_status_warning')}
      modalShow={showActiveStatusModal}
      btnSaveText={String(translate('journey_builder_component.btn_proceed_cem'))}
      btnCloseText={String(translate('journey_builder_component.btn_cancel_cem'))}
      onSaveModal={proceedActiveStatus}
      onCloseModal={cancelActiveStatus}
      alignToCentre
      data-testid="activeStatusEditWarningModalNodeConfirm"
      disableCloseBtn={false}
      disableSaveBtn={false}
      type="warning"
    />
  );
};

export const CemCancelWarningModal: React.FC<CemCancelWarningProps> = ({ showCancelWarningModal, leaveCancelWarningModal, setShowCancelWarningModal }: CemCancelWarningProps) => {
  const { t: translate } = useTranslation();

  const stayCancelWarningModal = () => {
    setShowCancelWarningModal(false);
  };

  return (
    <Modal
      modalTitle={translate('journey_builder_component.title_leave_confirm')}
      modalBody={translate('journey_builder_component.msg_leave_confirm')}
      modalShow={showCancelWarningModal}
      btnSaveText={String(translate('journey_builder_component.btn_confirm_leave_confirm'))}
      btnCloseText={String(translate('journey_builder_component.btn_close_confirm_confirm'))}
      onSaveModal={leaveCancelWarningModal}
      onCloseModal={stayCancelWarningModal}
      alignToCentre
      data-testid="cemCancelWarningModal"
      disableCloseBtn={false}
      disableSaveBtn={false}
      type="warning"
    />
  );
};

export const ApproveWarningModal: React.FC<ApproveWarningProps> = ({ showApproveWarningModal, journeyVersion, setShowApproveWarningModal, goToPrevPage, journeyName, journeyComments = undefined }: ApproveWarningProps) => {
  const { t: translate } = useTranslation();
  const toastMessage = useToastMessage();
  const loader = useLoader();

  /**
   * @method getLatestPendingApprovalDescription
   * @description Extracts the latest description from journeyStatusHistory for PENDING_APPROVAL status
   * @param journeyComments - Array of journey status history comments
   * @returns Latest description or empty string
   */
  const getLatestPendingApprovalDescription = (journeyComments?: JourneyComments[]): string => {
    if (!journeyComments || journeyComments.length === 0) {
      return '';
    }

    // Filter entries with PENDING_APPROVAL status
    const pendingApprovalEntries = journeyComments.filter(
      (entry) => entry.status?.toUpperCase() === 'PENDING_APPROVAL'
    );

    if (pendingApprovalEntries.length === 0) {
      return '';
    }

    // Sort by createdTimestamp descending to get the latest entry
    const sortedEntries = [...pendingApprovalEntries].sort((a, b) => {
      try {
        const timestampA = a.createdTimestamp ? new Date(a.createdTimestamp).getTime() : 0;
        const timestampB = b.createdTimestamp ? new Date(b.createdTimestamp).getTime() : 0;
        return timestampB - timestampA; // Descending order (latest first)
      } catch (error) {
        return 0;
      }
    });

    const latestEntry = sortedEntries[0];

    if (latestEntry.description && latestEntry.description.trim()) {
      return latestEntry.description.trim();
    }

    return '';
  };

  const latestDescription = getLatestPendingApprovalDescription(journeyComments);

  const approveWarningModalBody = journeyVersion > 1
    ? String(translate('journey_builder_component.msg_approve_edit_warning'))
    : String(translate('journey_builder_component.msg_approve_edit_no_ver_Warning'));

  // Build modal body - always use React element for consistency
  const infoIconText = String(translate('journey_builder_component.msg_version_description_info'));
  const versionDescriptionLabel = String(translate('journey_builder_component.lbl_version_description'));
  const noDescriptionText = String(translate('journey_builder_component.msg_no_description_provided'));
  const modalBodyContent = (
    <div className={classes.modal} data-testid="approveWarningModalWithDescription" style={{ position: 'relative' }}>
      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <div style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{versionDescriptionLabel}</span>
          <CIInfoIcon iconTxt={infoIconText} />
        </div>
        <p style={{ marginTop: '8px', marginBottom: '0', fontSize: '14px', lineHeight: '1.5', color: '#555' }}>{latestDescription || noDescriptionText}</p>
      </div>
      <p style={{ margin: 0 }}>{approveWarningModalBody}</p>
    </div>
  );

  const cancelApproveWarningModal = () => {
    setShowApproveWarningModal(false);
  };

  const proceedApproveWarningModal = () => {
    setShowApproveWarningModal(false);
    loader.show();
    (async () => {
      const resp = await changeJourneyStatus(journeyName, journeyVersion, { comment: 'approving', status: 'APPROVED' });
      if (resp) {
        loader.hide();
        goToPrevPage(true);
      } else {
        loader.hide();
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_approval_api_error'));
      }
    })();
  };

  useEffect(() => {
    if (showApproveWarningModal) {
      const styleId = 'approve-warning-modal-margin-fix';
      let styleElement = document.getElementById(styleId) as HTMLStyleElement;
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        [data-testid="approveWarningModalNodeConfirm"] .Modal_modal-dialog__KI4kC .Modal_modal-body__nc4sk.Modal_with-icon-body__GnJKz,
        [data-testid="approveWarningModalNodeConfirm"] .Modal_modal-body__nc4sk.Modal_with-icon-body__GnJKz {
          margin-left: 0px !important;
        }
        [data-testid="approveWarningModalNodeConfirm"] [class*="modal-body"][class*="with-icon-body"],
        [data-testid="approveWarningModalNodeConfirm"] [class*="Modal_modal-body"][class*="with-icon-body"] {
          margin-left: 0px !important;
        }
        /* Higher specificity override */
        [data-testid="approveWarningModalNodeConfirm"].Modal_modal-dialog__KI4kC .Modal_modal-body__nc4sk.Modal_with-icon-body__GnJKz,
        [data-testid="approveWarningModalNodeConfirm"] .Modal_modal-dialog__KI4kC .Modal_modal-body__nc4sk.Modal_with-icon-body__GnJKz.Modal_modal-body__nc4sk {
          margin-left: 0px !important;
        }
      `;
      document.head.appendChild(styleElement);
      const setMarginLeft = () => {
        // Try multiple ways to find the modal element
        const modalElement = document.querySelector('[data-testid="approveWarningModalNodeConfirm"]') || document.querySelector('.modal[data-testid*="approve"]') || document.body.querySelector('.modal.show');
        if (!modalElement) return;

        // Try multiple selector strategies
        let specificElement = modalElement.querySelector('.Modal_modal-dialog__KI4kC .Modal_modal-body__nc4sk.Modal_with-icon-body__GnJKz') as HTMLElement;
        if (!specificElement) {
          specificElement = modalElement.querySelector('.Modal_modal-body__nc4sk.Modal_with-icon-body__GnJKz') as HTMLElement;
        }
        if (!specificElement) {
          // Fallback: find any element with both classes
          const allElements = Array.from(modalElement.querySelectorAll('*'));
          const foundElement = allElements.find((el) => {
            const classes = (el as HTMLElement).className;
            return typeof classes === 'string' && classes.includes('Modal_modal-body__nc4sk') && classes.includes('Modal_with-icon-body__GnJKz');
          });
          if (foundElement) {
            specificElement = foundElement as HTMLElement;
          }
        }

        if (specificElement) {
          const computedStyle = window.getComputedStyle(specificElement);
          const currentMargin = computedStyle.marginLeft;
          if (currentMargin === '32px' || currentMargin !== '0px') {
            // Remove any existing margin-left from style
            specificElement.style.removeProperty('margin-left');
            // Set with !important using setProperty
            specificElement.style.setProperty('margin-left', '0px', 'important');
            // Also set via cssText as backup
            const existingStyle = specificElement.style.cssText;
            specificElement.style.cssText = `${existingStyle.replace(/margin-left[^;]*;?/gi, '')} margin-left: 0px !important;`;
          }
        }
        // Also check for elements with class patterns (fallback)
        const allElements = modalElement.querySelectorAll('*');
        allElements.forEach((element) => {
          const elementHTMLElement = element as HTMLElement;
          const elementClasses = elementHTMLElement.className;
          if (typeof elementClasses === 'string') {
            const hasModalBody = elementClasses.includes('modal-body') || elementClasses.includes('Modal_modal-body');
            const hasWithIconBody = elementClasses.includes('with-icon-body') || elementClasses.includes('Modal_with-icon-body');
            if (hasModalBody && hasWithIconBody) {
              const computedStyle = window.getComputedStyle(elementHTMLElement);
              if (computedStyle.marginLeft === '32px' || computedStyle.marginLeft !== '0px') {
                elementHTMLElement.style.cssText = `${elementHTMLElement.style.cssText.replace(/margin-left[^;]*;?/gi, '')} margin-left: 0px !important;`;
                const currentStyle = elementHTMLElement.getAttribute('style') || '';
                elementHTMLElement.setAttribute('style', `${currentStyle} margin-left: 0px !important;`);
              }
            }
          }
        });
      };
      const timeoutIds: ReturnType<typeof setTimeout>[] = [];
      const rafIds: number[] = [];
      const scheduleRaf = (cb: FrameRequestCallback) => {
        if (typeof requestAnimationFrame === 'undefined') return;
        const id = requestAnimationFrame(cb);
        rafIds.push(id);
      };
      const attemptSetMargin = () => {
        scheduleRaf(() => {
          setMarginLeft();
          scheduleRaf(() => {
            setMarginLeft();
          });
        });
      };
      const scheduleAttemptSetMargin = (delay: number) => {
        timeoutIds.push(setTimeout(attemptSetMargin, delay));
      };
      scheduleAttemptSetMargin(50);
      scheduleAttemptSetMargin(100);
      scheduleAttemptSetMargin(200);
      scheduleAttemptSetMargin(300);
      scheduleAttemptSetMargin(500);
      scheduleAttemptSetMargin(1000);
      const observer = new MutationObserver(() => {
        attemptSetMargin();
      });
      const modalElement = document.querySelector('[data-testid="approveWarningModalNodeConfirm"]');
      if (modalElement) {
        observer.observe(modalElement, {
          attributes: true,
          attributeFilter: ['style', 'class'],
          subtree: true,
          childList: true
        });
        timeoutIds.push(setTimeout(() => observer.disconnect(), 10000));
      }
      return () => {
        if (styleElement && styleElement.parentNode) {
          styleElement.parentNode.removeChild(styleElement);
        }
        observer.disconnect();
        timeoutIds.forEach((id) => clearTimeout(id));
        if (typeof cancelAnimationFrame !== 'undefined') {
          rafIds.forEach((id) => cancelAnimationFrame(id));
        }
      };
    }
    return undefined;
  }, [showApproveWarningModal]);

  return (
    <>
      {loader.render()}
      {toastMessage.renderToast()}
      <Modal
        modalTitle={translate('journey_builder_header_component.lbl_approve')}
        modalBody={modalBodyContent}
        modalShow={showApproveWarningModal}
        btnCloseText={String(translate('journey_builder_header_component.btn_no'))}
        btnSaveText={String(translate('journey_builder_header_component.btn_yes'))}
        onSaveModal={proceedApproveWarningModal}
        onCloseModal={cancelApproveWarningModal}
        alignToCentre
        data-testid="approveWarningModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />
    </>
  );
};

export const ReturnJourneyModal: React.FC<ReturnJourneyProps> = ({ showReturnModal, setShowReturnModal, journeyName, journeyVersion, goToPrevPage }: ReturnJourneyProps) => {
  const { t: translate } = useTranslation();
  const toastMessage = useToastMessage();
  const loader = useLoader();
  const [returnComment, setReturnComment] = useState<string>('');

  const handleCommentInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReturnComment(event.target.value);
  };

  const cancelReturnModal = () => {
    setShowReturnModal(false);
  };

  const submitReturnModal = () => {
    setShowReturnModal(false);
    loader.show();
    (async () => {
      const resp = await changeJourneyStatus(journeyName, journeyVersion, { comment: returnComment, status: 'REJECTED' });
      if (resp) {
        loader.hide();
        goToPrevPage(true);
      } else {
        loader.hide();
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_returned_api_error'));
      }
    })();
  };

  return (
    <>
      {loader.render()}
      {toastMessage.renderToast()}
      <Modal
        modalTitle={translate('journey_builder_header_component.lbl_return')}
        modalBody={
          <div className={classes.modal} data-testid="returnModal">
            <Textarea
              labelText={String(translate('journey_builder_header_component.txt_provide_reason'))}
              isMandatory
              helperText={String(translate('journey_builder_header_component.txt_helper_text'))}
              label=""
              value={returnComment}
              onChange={handleCommentInputChange}
              placeholder={String(translate('journey_builder_component.placeholder_txt'))}
              name="comment"
              maxLength={501}
              data-testid="auth-comment"
            />
          </div>
        }
        modalShow={showReturnModal}
        btnSaveText={String(translate('journey_builder_header_component.btn_submit'))}
        btnCloseText={String(translate('journey_builder_header_component.btn_cancel'))}
        onCloseModal={cancelReturnModal}
        onSaveModal={submitReturnModal}
        alignToCentre
        data-testid="returnReasonModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={!returnComment || returnComment.length === 501}
        type="warning"
      />
    </>
  );
};

export const SendForApproveModal: React.FC<SendForApproveProps> = ({ showSendForApprovalModal, journeyName, journeyVersion, setShowSendForApprovalWarningModal, goToPrevPage }: SendForApproveProps) => {
  const { t: translate } = useTranslation();
  const toastMessage = useToastMessage();
  const loader = useLoader();
  const [showDescriptionModal, setShowDescriptionModal] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');

  const cancelSendForApproveWarningModal = () => {
    setShowSendForApprovalWarningModal(false);
    setDescription('');
    setShowDescriptionModal(false);
  };

  const proceedSendForApproveWarningModal = () => {
    setShowSendForApprovalWarningModal(false);
    setShowDescriptionModal(true);
  };

  const handleDescriptionInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value);
  };

  const cancelDescriptionModal = () => {
    setShowDescriptionModal(false);
    setDescription('');
    setShowSendForApprovalWarningModal(false);
  };

  const submitDescriptionModal = () => {
    setShowDescriptionModal(false);
    loader.show();
    const descriptionValue = description.trim() || String(translate('journey_builder_component.msg_no_description_provided'));
    (async () => {
      const data = await changeJourneyStatus(journeyName, journeyVersion, {
        comment: 'sending for approval',
        status: 'PENDING_APPROVAL',
        description: descriptionValue
      });
      if (data) {
        loader.hide();
        setDescription('');
        goToPrevPage(true);
      } else {
        loader.hide();
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_send_for_approval_api_error'));
      }
    })();
  };

  return (
    <>
      {loader.render()}
      {toastMessage.renderToast()}
      <Modal
        modalTitle={translate('journey_builder_header_component.lbl_send_for_approval')}
        modalBody={translate('journey_builder_component.msg_send_for_approval_warning')}
        modalShow={showSendForApprovalModal && !showDescriptionModal}
        btnCloseText={String(translate('journey_builder_header_component.btn_no'))}
        btnSaveText={String(translate('journey_builder_header_component.btn_yes'))}
        onSaveModal={proceedSendForApproveWarningModal}
        onCloseModal={cancelSendForApproveWarningModal}
        alignToCentre
        data-testid="sendForApprovalWarningModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />
      <Modal
        modalTitle={translate('journey_builder_header_component.lbl_send_for_approval')}
        modalBody={
          <div className={classes.modal} data-testid="sendForApprovalDescriptionModal">
            <Textarea
              labelText={String(translate('journey_builder_component.txt_description'))}
              isMandatory={false}
              helperText=""
              label=""
              value={description}
              onChange={handleDescriptionInputChange}
              placeholder={String(translate('journey_builder_component.txt_desc_placeholder'))}
              name="description"
              maxLength={501}
              data-testid="send-for-approval-description"
            />
          </div>
        }
        modalShow={showDescriptionModal}
        btnSaveText={String(translate('journey_builder_header_component.btn_submit'))}
        btnCloseText={String(translate('journey_builder_header_component.btn_cancel'))}
        onCloseModal={cancelDescriptionModal}
        onSaveModal={submitDescriptionModal}
        alignToCentre
        data-testid="sendForApprovalDescriptionModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />
    </>
  );
};

export const MoveToDraftModal: React.FC<MoveToDraftProps> = ({ showMoveToDraftModal, journeyName, journeyVersion, setShowMoveToDraftWarningModal, goToPrevPage }: MoveToDraftProps) => {
  const { t: translate } = useTranslation();
  const toastMessage = useToastMessage();
  const loader = useLoader();

  const cancelMoveToDraftWarningModal = () => {
    setShowMoveToDraftWarningModal(false);
  };

  const proceedMoveToDraftWarningModal = () => {
    setShowMoveToDraftWarningModal(false);
    loader.show();
    (async () => {
      const data = await changeJourneyStatus(journeyName, journeyVersion, { comment: 'cancelling review', status: 'REVIEW_CANCEL' });
      if (data) {
        loader.hide();
        goToPrevPage(true);
      } else {
        loader.hide();
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_move_to_draft_api_error'));
      }
    })();
  };

  return (
    <>
      {loader.render()}
      {toastMessage.renderToast()}
      <Modal
        modalTitle={translate('journey_builder_header_component.btn_draft')}
        modalBody={translate('journey_builder_component.msg_move_to_draft_warning')}
        modalShow={showMoveToDraftModal}
        btnCloseText={String(translate('journey_builder_header_component.btn_no'))}
        btnSaveText={String(translate('journey_builder_header_component.btn_yes'))}
        onSaveModal={proceedMoveToDraftWarningModal}
        onCloseModal={cancelMoveToDraftWarningModal}
        alignToCentre
        data-testid="moveToDraftWarningModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />
    </>
  );
};

export const SaveJourneyToDesignCemModal: React.FC<SaveJourneyToDesignCemProps> = ({ showSaveJourneyToDesignCemModal, closeSaveJourneyModal }: SaveJourneyToDesignCemProps) => {
  const { t: translate } = useTranslation();
  const saveJourneyToDesignCEM = (
    <p>{translate('journey_builder_component.msg_save_journey_to_design_cem')}</p>
  );

  return (
    <Modal
      modalTitle={translate('journey_builder_component.lbl_save_journey_title')}
      modalBody={saveJourneyToDesignCEM}
      modalShow={showSaveJourneyToDesignCemModal}
      btnCloseText={String(translate('journey_builder_component.lbl_save_journey'))}
      onCloseModal={closeSaveJourneyModal}
      alignToCentre
      data-testid="saveJourneyToDesignCEM"
      disableCloseBtn={false}
    />
  );
};

export const ConfirmDeleteModal: React.FC<ConfirmDeleteProps> = ({ notifyDialogStatus, designingCEM, showNotifyDialog, confirmDelete, hideDeleteNotifyDialogHandler }: ConfirmDeleteProps) => {
  const { t: translate } = useTranslation();

  /**
   * @method getNotifyDeleteDialogMessage
   * @returns message depending on which delete button is clicked
   */
  const getNotifyDeleteDialogMessage = () => {
    switch (notifyDialogStatus) {
      case NotifyDialogStatus.DELETE_CEM:
        return translate('journey_builder_component.msg_notify_delete_cem');
      case NotifyDialogStatus.DELETE_NODE:
        if (!designingCEM) {
          return translate('journey_builder_component.msg_notify_delete_node');
        }
        return translate('journey_builder_component.msg_notify_delete_node_on_cem');
      case NotifyDialogStatus.DELETE_STEP:
        return translate('journey_builder_component.msg_notify_delete_step');
      case NotifyDialogStatus.DELETE_SUBSEQUENT_STEP:
        return translate('journey_builder_component.msg_notify_delete_subsequent_step');
      default:
        return translate('journey_builder_component.msg_notify_delete_connector');
    }
  };

  return (
    <Modal
      modalTitle={String(translate('journey_builder_component.notify_header_message'))}
      modalBody={getNotifyDeleteDialogMessage()}
      modalShow={showNotifyDialog}
      btnSaveText={String(translate('journey_builder_component.btn_delete'))}
      btnCloseText={String(translate('journey_builder_component.txt_donot_delete'))}
      onSaveModal={confirmDelete}
      onCloseModal={hideDeleteNotifyDialogHandler}
      alignToCentre
      disableSaveBtn={false}
      disableCloseBtn={false}
      type="warning"
    />
  );
};

export const ExternalOnSubsequentStepModal: React.FC<ExternalOnSubsequentStepModalProps> = ({ show, onDismiss }: ExternalOnSubsequentStepModalProps) => {
  const { t: translate } = useTranslation();

  return (
    <Modal
      modalTitle={translate('journey_builder_component.title_external_subsequent_step_modal')}
      modalBody={translate('journey_builder_component.msg_external_subsequent_step_modal')}
      modalShow={show}
      btnCloseText={String(translate('journey_builder_component.btn_close'))}
      onCloseModal={onDismiss}
      alignToCentre
      data-testid="cemCancelWarningModal"
      disableCloseBtn={false}
      type="warning"
    />
  );
};
